import { useEffect, useState, useRef } from "preact/hooks";
import { Fragment, h, render } from "preact";
import { FreeMode, Mousewheel } from "swiper/modules";
import { Plus, Minus } from "../icons.jsx";
import storefront from "../storefront";
import * as queries from "../storefront/queries";
import stockMessage from "../stock-message";
import getSellingPlanId from "../getSellingPlanId.js";

const RelatedProductsContent = ({ data, collection }) => {
  const [cartItems, setCartItems] = useState(data?.items.reverse());
  const [init, setInit] = useState(false);
  const [relatedItems, setRelatedItems] = useState(null);
  const [productId, setProductId] = useState(cartItems[0]?.product_id);
  const [swiper, setSwiper] = useState(null);
  const [open, setOpen] = useState(true);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const newCartItems = data?.items.reverse();
    setCartItems(newCartItems);
    setProductId(newCartItems[0]?.product_id);
  }, [data.items]);

  useEffect(() => {
    if (!relatedItems?.length) {
      setOpen(false);
    }
  }, [relatedItems]);

  const swiperParams = {
    slidesPerView: 1,
    spaceBetween: 16,
    rebuildOnUpdate: true,
    mousewheel: {
      forceToAxis: true,
    },
  };

  const getProductsByCollection = async () => {
    if (!collection) return;
    let items = collection.split(",");
    getFullProducts(items);
  };

  const getRelatedImage = (item) => {
    let image_src = item.featured_image
      ? item.featured_image
      : item?.images?.edges
        ? item?.images?.edges[0].node.src
        : null;

    return (
      image_src && (
        <img
          src={getSizedImageUrl(image_src, "small")}
          alt={item.title}
          width="80"
          height="80"
        />
      )
    );
  };

  const getFullProducts = async (items) => {
    const response = await storefront.request({
      query: queries.products,
      variables: {
        ids: items.map((id) => encodeId(id, "Product")),
      },
    });
    if (response?.data?.nodes) {
      const products = response.data.nodes.filter(
        (p) => p && !p.tags.includes("hide-from-search"),
      );

      setRelatedItems(products);
    }
  };

  const getProducts = () => {
    if (!productId) return;
    fetch(
      `/recommendations/products.json?product_id=${productId}&limit=5&intent=related`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
      .then((data) => data?.json())
      .then((response) => {
        if (!response) return;
        let items = response.products.filter(
          ({ id, handle }) =>
            !cartItems.some(
              (e) => e.product_id === id || e.properties?._bundle === handle,
            ),
        );
        items = items.map((item) => item.id);
        getFullProducts(items);
      });
  };

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleResize = () => {
    setOpen(window.innerHeight > 800);
    setShow(window.innerHeight > 640);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(async () => {
    if (productId) {
      getProducts();
    } else {
      getProductsByCollection();
    }
  }, [cartItems]);

  const addRelatedItem = (item) => {
    let v = item?.variants[0]
      ? item?.variants[0]
      : item?.variants?.edges[0].node;
    let vid = item?.variants[0]?.id,
      alt_vid = item?.variants?.edges
        ? item?.variants?.edges[0].node.id.split("/").pop()
        : null,
      variant = vid ? vid : alt_vid;

    if (!variant) return;
    const inventoryReno = v.inventoryReno?.value
      ? parseInt(v.inventoryReno.value)
      : null;
    const inventoryAtlanta = v.inventoryAtlanta?.value
      ? parseInt(v.inventoryAtlanta.value)
      : null;

    const properties = {
      _inventoryReno: inventoryReno,
      _inventoryAtlanta: inventoryAtlanta,
      _selling_plan: getSellingPlanId(v),
      _added_by: "RelatedProductsContent addRelatedItem",
    };

    window.addToCart(
      {
        id: variant,
        quantity: 1,
        properties,
        selling_plan: getSellingPlanId(v),
      },
      {
        onError: (response) => console.error(response),
      },
    );
  };

  return (
    show &&
    relatedItems &&
    relatedItems.length > 0 && (
      <Fragment>
        <button
          className={`main-cart__ymal__heading`}
          onClick={() => {
            handleOpen();
          }}
        >
          <span className="h5">You May Also Like</span>
          {open ? <Minus /> : <Plus />}
        </button>
        <div
          className={`main-cart__ymal__container ${open ? " is-open" : ""}`}
          onFocus={() => {
            setOpen(true);
          }}
        >
          <swiper-container
            {...swiperParams}
            modules={[FreeMode, Mousewheel]}
            className="related-products__container"
          >
            {relatedItems.map((item, index) => (
              <swiper-slide className="swiper-slide">
                <div className="product-form__option">
                  <label>
                    <div className="product-form__option-text">
                      <span>{item.title}</span>
                      <span className="product-form__option-price">
                        {window.formatMoney(
                          item.price
                            ? item.price / 100
                            : item.variants?.edges[0].node.priceV2.amount,
                        )}
                      </span>
                      <span
                        className="product-form__option-availability caption-small"
                        data-stock
                      >
                        {item?.variants?.edges.length > 0 &&
                          stockMessage(item.variants.edges[0].node)}
                      </span>
                      <span>
                        <button
                          className="product-related__add"
                          add-to-cart
                          name="form"
                          onClick={() => addRelatedItem(item)}
                          aria-label="Add product to cart"
                        >
                          <span className="product-related__icon">
                            <Plus />
                          </span>
                          <span>Add to Cart</span>
                        </button>
                      </span>
                    </div>
                    <a
                      href={item.url ? item.url : `/products/${item.handle}`}
                      className="product-form__option-image"
                    >
                      {getRelatedImage(item)}
                    </a>
                  </label>
                </div>
              </swiper-slide>
            ))}
          </swiper-container>
        </div>
      </Fragment>
    )
  );
};

export default RelatedProductsContent;
